/**
 * Identifiers a confirmed subject's evidence carries, offered as the next thing to search.
 *
 * **The platform suggests; a person decides and attests.** Nothing here opens a case, adds
 * a subject or collects anything. A researcher takes an identifier, writes an
 * authorisation attestation and runs it — the deliberate act ADR-0024 requires of every
 * collection, and the reason this panel ends at a copyable value rather than a button that
 * starts work.
 *
 * **Why it appears only after the subject is confirmed.** A discovered identifier is not
 * an authorisation to research whoever holds it. Identifiers arrive on the same
 * observation as every other candidate a name search returned: one real observation held a
 * single ORCID beside thirteen different people's names, and it belonged to an academic
 * surfaced by an unrelated investigation because her surname matched. Offering that as a
 * subject would invite collecting on somebody nobody named.
 *
 * `confirmed_by_researcher` is the one status that means *this is the person* — no
 * arithmetic reaches it (ADR-0008) — so before it is set, the panel says what is missing
 * rather than showing an empty list. "Nothing to suggest" and "you have not said who this
 * is yet" are different answers.
 */

import type { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError, request } from "@/lib/api";
import { Badge, EmptyState, ErrorState, Loading } from "@/components/ui";

interface Suggestion {
  assertion_id: string;
  identifier_type: string;
  identifier_value: string;
  source: string;
  already_a_subject: boolean;
  information_credibility: number;
}

/**
 * The suggested-subjects panel.
 *
 * @param props - The subject to read.
 * @returns The panel.
 */
export function SubjectSuggestionsPanel({ subjectId }: { subjectId: string }): ReactElement {
  const suggestions = useQuery({
    queryKey: ["subject-suggestions", subjectId],
    queryFn: () => request<Suggestion[]>(`/research/subjects/${subjectId}/suggested-subjects`),
    retry: false,
  });

  if (suggestions.isLoading) return <Loading label="Loading identifiers" />;

  // A 409 is the confirmation gate, not a fault. It carries its own reasoning, and
  // showing that reasoning is the point — a researcher who sees "confirm this subject
  // first" learns what the gate is for.
  if (suggestions.error instanceof ApiError && suggestions.error.status === 409) {
    return (
      <EmptyState>
        Confirm this subject before identifiers are suggested. Until somebody has said this
        is the person, an identifier found in the evidence may belong to any of the
        candidates a search returned — and researching one of those is not something this
        workspace will propose on its own.
      </EmptyState>
    );
  }
  if (suggestions.error != null) return <ErrorState error={suggestions.error} />;

  const rows = suggestions.data ?? [];

  return (
    <section
      className="space-y-3"
      aria-label="Suggested subjects panel"
      data-testid="subject-panel-suggestions"
    >
      <p className="max-w-2xl text-xs text-muted">
        Identifiers this person's attached evidence carries. Researching one means opening a
        case with it and attesting to why — nothing here starts a collection.
      </p>

      {rows.length === 0 ? (
        <EmptyState>
          No searchable identifiers in the attached evidence. Names and profile URLs are not
          offered: a name is what produced the candidates, and a profile URL identifies an
          account rather than a person.
        </EmptyState>
      ) : (
        <ul className="space-y-2" data-testid="suggestion-list">
          {rows.map((row) => (
            <li
              key={row.assertion_id}
              className="flex flex-wrap items-center gap-2 rounded border border-line p-3"
              data-testid="suggestion-row"
            >
              <Badge tone="muted">{row.identifier_type}</Badge>
              <span className="font-mono text-xs">{row.identifier_value}</span>
              <span className="text-xs text-muted">via {row.source}</span>
              <span className="text-xs text-muted">credibility {row.information_credibility}</span>
              {row.already_a_subject && (
                // Wrapped rather than passing the attribute to `Badge`, which does not
                // forward one — a shared component should not grow a prop to suit a test.
                <span data-testid="already-a-subject">
                  <Badge tone="muted">already investigated</Badge>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
